import { Component, OnInit, Inject } from '@angular/core';
import { FormControl, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import mixpanel from 'mixpanel-browser';
import { TranslateService } from '@ngx-translate/core';

import { DataService } from '@app-core/services/data/data.service';
import { VIDEO_FORMATS, DVR_RESOLUTION_OPTIONS } from '@app-core/constants/constants';
import { PreferencesConfig, Resolutions } from '@app-core/models/core.model';
import { AccessService } from '@app-core/services/access/access.service';
import { TripsService } from '@app-trips/services/trips.service';
import { AssetConfigurationService } from '@app-asset-config/services/asset-configuration.service';

import * as moment from 'moment';

const RESOLUTION_1080 = '1920x1080';
const RESOLUTION_720 = '1280x720';

@Component({
  selector: 'app-time-lapse-dvr-modal-component',
  templateUrl: './time-lapse-dvr-modal-component.component.html',
  styleUrls: ['./time-lapse-dvr-modal-component.component.scss'],
})
export class TimeLapseDvrModalComponentComponent implements OnInit {
  public loader: boolean = false;
  public isError: boolean = false;
  public isResponse: boolean = false;
  public dvrVideoFormats = VIDEO_FORMATS;
  public selectedVideoFormat = new FormControl();
  public form: FormGroup;
  private preferences: PreferencesConfig;
  public dvrResolutions: Resolutions[] = DVR_RESOLUTION_OPTIONS;

  constructor(
    public dialogRef: MatDialogRef<TimeLapseDvrModalComponentComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dataService: DataService,
    private fb: FormBuilder,
    private accessService: AccessService,
    private tripsService: TripsService,
    public translate: TranslateService,
    private assetConfigurationService: AssetConfigurationService
  ) {}

  ngOnInit(): void {
    this.generateFormValue();
    if (this.data?.assetId) {
      this.assetConfigurationService
        .getAssetConfiguration({ assetId: this.data.assetId })
        .pipe(catchError(() => of({ configuration: {} })))
        .subscribe((res: any) => {
          if (res?.configuration?.enableDriverCamera === false) {
            this.applyDriverCameraFilter();
          }
        });
    }
  }

  private applyDriverCameraFilter() {
    const driverCameraFormats = ['driver', 'sideBySide', 'pictureInPictureRoadSide', 'pictureInPictureDriverSide', 'separate'];
    this.dvrVideoFormats = this.dvrVideoFormats.filter((v) => !driverCameraFormats.includes(v.value));
    const currentFormat = this.form.get('videoFormat')?.value;
    if (driverCameraFormats.includes(currentFormat)) {
      this.form.patchValue({ videoFormat: this.dvrVideoFormats[0]?.value });
    }
  }

  private generateFormValue() {
    this.preferences = this.dataService.fleetPreferences?.dvrVideo;
    this.dvrResolutions = this.dataService.getResolutionsBetween(
      this.dvrResolutions,
      this.preferences.maxAllowedResolution
    ) as Resolutions[];

    const resolution = this.dvrResolutions.find((res) => res.value === this.preferences.defaultResolution) || null;
    const defaultResolution = resolution ? this.preferences.defaultResolution : this.dvrResolutions[this.dvrResolutions.length - 1].value;

    this.dvrVideoFormats = this.dataService.getDvrVideoFormat(VIDEO_FORMATS, this.preferences);
    this.showHDResolutions(this.dvrVideoFormats[0].value);

    this.form = this.fb.group({
      videoResolution: this.fb.control(defaultResolution, [Validators.required]),
      videoFormat: this.fb.control(this.dvrVideoFormats[0].value, [Validators.required]),
    });
  }

  private showHDResolutions(videoFormat) {
    this.preferences = this.dataService.fleetPreferences?.dvrVideo;

    if (videoFormat === 'sideBySide') {
      this.dvrResolutions = this.dvrResolutions.filter((x: Resolutions) => x.value !== RESOLUTION_1080) as Resolutions[];
      this.lowerVideoResolutionForSideBySide();
    } else {
      this.dvrResolutions = this.dvrResolutions.find((res) => res.value === RESOLUTION_1080)
        ? this.dvrResolutions
        : [...this.dvrResolutions, { value: RESOLUTION_1080, text: RESOLUTION_1080 }];
    }
  }

  private lowerVideoResolutionForSideBySide() {
    const videoResolutionSetting = this.form.get('videoResolution');
    if (videoResolutionSetting?.value === RESOLUTION_1080) {
      videoResolutionSetting.patchValue(RESOLUTION_720, { emitEvent: false, onlySelf: false });
    }
  }

  public onSubmit() {
    this.loader = true;
    const { tripDetails = {}, selectedTimeUTC, dvrDuration, dvrVideoType = 'road' } = this.data || {};

    const { tripId = '', driverId = '', endTimeUTC } = tripDetails || {};
    let selectedEndTimeUTC = moment(selectedTimeUTC).add(dvrDuration, 'seconds').toISOString();
    selectedEndTimeUTC = new Date(selectedEndTimeUTC) > new Date(endTimeUTC) ? endTimeUTC : selectedEndTimeUTC;

    const videoType =
      dvrVideoType === 'pictureInPictureRoadSide' || dvrVideoType === 'pictureInPictureDriverSide' ? 'pictureInPicture' : dvrVideoType;

    const { videoResolution = '', videoFormat = '' } = this.form.value;
    const { loginName: userEmail, name: userName, loginType, userId, userHash } = this.accessService.getLoginInfo();

    const body = {
      tripId,
      driverId,
      startTimeUTC: selectedTimeUTC,
      endTimeUTC: selectedEndTimeUTC,
      videoResolution: videoResolution,
      enabledTimelapse: false,
      dvrVideoType: videoFormat,
      dvrInitiatedMetadata: {
        userType: loginType, // this loginType is changed for notification purpose
        email: userEmail,
        name: userName,
        ...(userHash ? { ssouserHash: userHash } : { userId }),
      },
    };

    this.tripsService
      .createDvrRequest(body)
      .pipe(
        finalize(() => {
          this.loader = false;
          this.isResponse = true;
        })
      )
      .subscribe(
        () => {
          this.isError = false;
          mixpanel.track('Video Request Submitted', {
            'Video Resolution': videoResolution,
            'Trip Id': tripId,
            'Driver Id': driverId,
            'DVR Duration': dvrDuration,
            'DVR VideoType': videoType,
            'UTC Time': selectedTimeUTC,
            'Local Time': selectedEndTimeUTC,
            'Submission Status': 'success',
          });
        },
        () => {
          this.isError = true;
          mixpanel.track('Video Request Submission Failed', {
            'Video Resolution': videoResolution,
            'Trip Id': tripId,
            'Driver Id': driverId,
            'DVR Duration': dvrDuration,
            'DVR VideoType': videoType,
            'UTC Time': selectedTimeUTC,
            'Local Time': selectedEndTimeUTC,
            'Submission Status': 'Failed',
          });
        }
      );
  }
}
